import React from "react";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { Check, X, AlertTriangle, Ban, Circle, Dot, type LucideIcon } from "lucide-react";
import { cn } from "@/lib/utils";

export type StatusKind = "met" | "not" | "exc" | "ex" | "den" | "ip";

const STATUS_META: Record<StatusKind, { label: string; colorVar: string; Icon: LucideIcon }> = {
  met: { label: "Numerator Met", colorVar: "--status-met", Icon: Check },
  not: { label: "Numerator Not Met", colorVar: "--status-not", Icon: X },
  exc: { label: "Numerator Exception", colorVar: "--status-exc", Icon: AlertTriangle },
  ex: { label: "Denominator Exclusion", colorVar: "--status-exclusion", Icon: Ban },
  den: { label: "Denominator", colorVar: "--status-den", Icon: Circle },
  ip: { label: "Initial Population", colorVar: "--status-ip", Icon: Dot },
};

interface StatusBubbleProps extends React.HTMLAttributes<HTMLButtonElement> {
  status: StatusKind;
  active: boolean;
  disabled?: boolean;
  hasEvidence?: boolean;
  label?: string;
  timestamp?: string | number;
  source?: string;
  onClick?: () => void;
  onOpenDetail?: () => void;
}

export default function StatusBubble({
  status,
  active,
  disabled,
  hasEvidence,
  label,
  timestamp,
  source,
  onClick,
  onOpenDetail,
  className,
  ...rest
}: StatusBubbleProps) {
  const meta = STATUS_META[status];
  const colorVar = meta.colorVar;

  const content = (
    <button
      type="button"
      aria-label={label || meta.label}
      aria-pressed={active}
      disabled={disabled}
      onClick={onClick}
      className={cn(
        "status-bubble",
        "relative inline-flex h-6 w-6 items-center justify-center rounded-full border-2",
        "transition-all duration-150",
        "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-muted",
        !disabled && "hover:scale-105 active:scale-95 cursor-pointer",
        active ? "text-white shadow-sm" : "text-foreground/60 bg-transparent",
        `status-${status}`,
        active && `status-${status}-active`,
        disabled && "opacity-50 cursor-not-allowed",
        className
      )}
      data-active={active}
      data-status={status}
      style={{
        "--bubble-color": `hsl(var(${colorVar}))`,
        backgroundColor: active ? `hsl(var(${colorVar}))` : "transparent",
        borderColor: active ? `hsl(var(${colorVar}))` : "hsl(var(--status-outline))"
      } as React.CSSProperties}
      {...rest}
    >
      {active ? <meta.Icon className="h-4 w-4" /> : null}
      {hasEvidence ? (
        <span
          className="evidence-indicator absolute -top-0.5 -right-0.5 h-1.5 w-1.5 rounded-full border border-background animate-pulse"
          style={{ backgroundColor: `hsl(var(${colorVar}))` }}
        />
      ) : null}
    </button>
  );

  return (
    <TooltipProvider>
      <Tooltip>
        <TooltipTrigger asChild>{content}</TooltipTrigger>
        <TooltipContent side="top" align="center" className="z-50">
          <div className="text-xs space-y-1">
            <div className="font-medium">{label || meta.label}</div>
            <div className="text-muted-foreground">
              {timestamp ? <span>Timestamp: {String(timestamp)}</span> : null}
              {timestamp && source ? <span> • </span> : null}
              {source ? <span>Source: {source}</span> : null}
            </div>
            {onOpenDetail && (
              <div className="pt-1 flex items-center gap-2">
                <button onClick={onOpenDetail} className="text-primary hover:underline">Open details</button>
              </div>
            )}
          </div>
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
}



